// The size of the interface, as the desktop viewer's UI Size setting: every
// window, bar, menu and button drawn larger or smaller, while the world
// behind them, the name tags in it and its picture keep their size.
//
// The interface layer is zoomed. Zoomed, its own lengths are in its own
// pixels while the pointer and the screen are still measured in the
// screen's, so anything that puts an element at the pointer, or measures
// the screen to place one, goes through `toUi` and `uiViewport` here.

import { loadSetting, saveSetting } from "./dom";

/** The smallest and largest the interface may be made, as a multiple of its own size. */
export const UI_SCALE_MIN = 0.75;
export const UI_SCALE_MAX = 1.5;

let scale = 1;

/** The interface's size now, as a multiple of its own. */
export function uiScale(): number {
  return scale;
}

/**
 * Make the interface larger or smaller. Windows are put back on the screen
 * afterwards, as they are when the browser window changes size, since one
 * near an edge may now hang over it.
 */
export function setUiScale(value: number, save = true): void {
  const next = Number.isFinite(value) ? Math.round(Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN, value)) * 20) / 20 : 1;
  if (save) saveSetting("ui.scale", next);
  if (next === scale) return;
  scale = next;
  const layer = document.getElementById("ui");
  if (layer) layer.style.zoom = next === 1 ? "" : String(next);
  document.documentElement.style.setProperty("--ui-scale", String(next));
  window.dispatchEvent(new Event("resize"));
}

/** The size kept in this browser, put back at start-up. */
export function applySavedUiScale(): void {
  setUiScale(loadSetting("ui.scale", 1), false);
}

/** A distance in screen pixels, as the interface measures it. */
export function toUi(px: number): number {
  return px / scale;
}

/** The screen's size, as the interface measures it. */
export function uiViewport(): { width: number; height: number } {
  return { width: (window.innerWidth || 1280) / scale, height: (window.innerHeight || 720) / scale };
}

/**
 * The interface layer, drawn at the interface's size. Anything floating
 * over the page, a menu, a popup or a dialog, goes in it so as to be the
 * same size as everything else, and is placed with `toUi`.
 */
export function uiLayer(): HTMLElement {
  return document.getElementById("ui") ?? document.body;
}
